<div class="my-5">
  <h1>Secure Choice Lending Privacy Policy</h1>
  <p><strong>Effective Date: 3/26/2026</strong></p>
  <p>Secure Choice Lending, a California corporation ("SCL," "we," "us," or "our"), respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and protect personal information across our mortgage services, website, CRM systems, marketing platforms, AI tools, referral partner tools, and promotional campaigns.</p>

  <h5>1. Scope of This Policy</h5>
  <p>This policy applies to:</p>
  <ul>
    <li>Website visitors</li> 
    <li>Mortgage applicants, borrowers, and co-borrowers</li>
    <li>Leads and prospective customers</li>
    <li>Referral partners (including real estate agents and brokers)</li>
    <li>Users of SCL platforms, CRM systems, and tools</li>
    <li>Participants in promotions, sweepstakes, and giveaways</li>
  </ul>

  <h5>Your Data &amp; Our Commitment</h5>
  <p>We take your privacy seriously. We do not sell your personal information for monetary compensation, and we do not use your data outside of the purposes described in this Privacy Policy.</p>
  <p>Personal information you provide is used to:</p>
  <ul>
    <li>Deliver mortgage and lending services</li>
    <li>Operate our systems and tools</li>
    <li>Communicate with you regarding your loan and related opportunities</li>
    <li>Provide marketing and educational content where permitted</li>
  </ul>
  <p>We do not allow third parties to use your personal information for their own independent marketing purposes.</p>

  <h5>2. Categories of Personal Information We Collect</h5>
  <p><strong>A. Identifiers</strong></p>
  <p>Name, email address, phone number, mailing address, IP address, device identifiers</p>
  <p><strong>B. Financial &amp; Lending Information</strong></p>
  <p>Income, assets, employment data, credit information, loan details, property data</p>
  <p><strong>C. Commercial Information</strong></p>
  <p>Products or services considered, transaction history, loan activity</p>
  <p><strong>D. Internet / Network Activity</strong></p>
  <p>Website interactions, cookies, analytics data, CRM engagement (opens, clicks, responses), communication history (emails, messages), campaign activity, workflow data, and interaction records generated through SCL systems</p>
  <p><strong>E. Professional Information</strong></p>
  <p>Real estate agent or broker information, business contact details</p>
  <p><strong>F. Marketing &amp; Communication Data</strong></p>
  <p>Preferences, opt-ins, engagement history</p>
  <p><strong>G. Sensitive Personal Information</strong></p>
  <p>Government identification, financial account data (collected only as necessary for lending)</p>

  <h5>3. Sources of Information</h5>
  <p>We collect information from:</p>
  <ul>
    <li>You directly (forms, applications, communications)</li>
    <li>Automated technologies (cookies, tracking pixels, analytics tools)</li>
    <li>Advertising platforms (Google, Meta/Facebook, LinkedIn)</li>
    <li>Third parties (credit bureaus, verification services, service providers)</li>
    <li>Referral partners</li>
    <li>CRM and marketing platforms</li>
  </ul>

  <h5>4. How We Use Personal Information</h5>
  <p>We use personal information for the following business purposes:</p>
  <ul>
    <li>Mortgage origination, underwriting, and servicing support</li>
    <li>Communicating about loan applications and transactions</li>
    <li>Marketing, advertising, and drip campaigns</li>
    <li>CRM automation and lead nurturing</li>
    <li>Operating referral partner tools and systems</li>
    <li>AI-assisted communication and outreach</li>
    <li>Fraud detection, security, and compliance</li>
    <li>Legal and regulatory compliance</li>
  </ul>

  <h5>5. AI and Automated Communications</h5>
  <p>Secure Choice Lending uses artificial intelligence, machine learning, and automated systems to assist with customer communications and marketing outreach. This includes:</p>
  <ul>
    <li>Email communications generated or personalized using AI</li>
    <li>SMS/text messages sent using automated systems (where consent is provided)</li>
    <li>Automated dialing systems and prerecorded/artificial voice messages where permitted by law</li>
  </ul>
  <p>You may receive communications that are generated or delivered in whole or in part by AI, without direct human involvement in each individual message.</p>
  <p>You may opt out of marketing communications at any time.</p>

  <h5>5A. AI-Assisted Lending Support</h5>
  <p>SCL may use AI and automated systems to assist with certain lending-related functions, including:</p>
  <ul>
    <li>Preliminary eligibility screening</li>
    <li>Loan product and rate scenario recommendations</li>
    <li>Document classification and data extraction</li>
    <li>Risk analysis and fraud detection</li>
  </ul>
  <p><strong>Human Oversight:</strong> These tools assist human decision-makers. Final lending decisions are reviewed by qualified SCL personnel and are not made solely by automated systems.</p>
  <p><strong>Requests for Review:</strong> If you receive an adverse action notice in connection with a lending decision that involved automated processing, you may request:</p>
  <ul>
    <li>Review of that decision by a human representative</li>
    <li>Additional information regarding the principal factors that influenced the decision</li>
    <li>Correction of inaccurate information that may have affected the outcome</li>
  </ul>
  <p>To make a request, contact us at info&#64;securechoicelending.com or 951-733-8925. We will respond within a reasonable time and in accordance with applicable law.</p>
  <p>Nothing in this section limits your rights under the Equal Credit Opportunity Act (ECOA), the Fair Credit Reporting Act (FCRA), or any applicable state law.</p>

  <h5>6. Sharing and Disclosure of Information</h5>
  <p>We may disclose personal information to the following categories of recipients:</p>
  <p><strong>A. Service Providers / Contractors</strong></p>
  <ul>
    <li>CRM systems</li>
    <li>Cloud storage providers</li>
    <li>Email and SMS platforms</li>
    <li>AI and automation providers</li>
    <li>Analytics and advertising platforms</li>
  </ul>
  <p>These parties are contractually restricted from using your data for their own purposes. We share only the minimum amount of personal information necessary to provide services and operate our systems.</p>
  <p><strong>B. Advertising &amp; Analytics ("Sharing")</strong></p>
  <p>We may share limited data with advertising platforms (such as Google, Meta, and LinkedIn) for marketing and audience targeting purposes. This may be considered "sharing" under California law.</p>
  <p><strong>C. Lending Participants</strong></p>
  <p>Investors, lenders, underwriters, and fulfillment partners necessary to complete your loan</p>
  <p><strong>D. Referral Partners</strong></p>
  <p>Real estate agents may have access to information related to their own referred clients solely for transaction coordination and business purposes. Access is provided solely for legitimate business purposes related to the transaction and not in exchange for referrals. They do not have access to unrelated consumer data.</p>
  <p><strong>E. Legal / Regulatory</strong></p>
  <p>Government agencies, regulators, or legal authorities as required</p>
  <p>We do not sell personal information for monetary compensation.</p>
  <p><strong>F. Third-Party and Related Service Providers</strong></p>
  <p>As part of providing our services, Secure Choice Lending may share certain personal information with third-party service providers to assist with services related to your transaction or to provide additional resources that may be relevant to you.</p>
  <p>These services may include, but are not limited to, insurance, title, escrow, or other related services.</p>
  <p>Such referrals or introductions are provided for your convenience only. You are under no obligation to use any recommended service provider and are free to select any provider of your choice.</p>
  <p>In some cases, Secure Choice Lending may refer you to service providers that are affiliated with, owned by, or under common control with SCL. Where required by law, we will provide appropriate disclosures regarding such relationships.</p>
  <p>Secure Choice Lending does not require the use of any specific service provider as a condition of obtaining a loan or any other service.</p>

  <h5>7. CRM and Referral Partner Access</h5>
  <p>SCL provides tools and systems to referral partners. These systems:</p>
  <ul>
    <li>Allow partners to view only their own leads and related data</li>
    <li>May allow associated loan officers to access shared relationship data</li>
    <li>Are governed by internal access controls and contractual restrictions</li>
  </ul>
  <p>Access to SCL tools, systems, or platforms is provided for legitimate business and marketing purposes, including education, brand development, and operational support.</p>
  <p>Access is not conditioned on the referral of business, and no agreement, expectation, or requirement exists for any partner to refer mortgage transactions to Secure Choice Lending.</p>
  <p>SCL may require referral partners to enter into agreements governing the use of data and platform access.</p>
  <p>All users are prohibited from using data outside permitted business purposes.</p>

  <h5>8. User-Generated Content and Platform Activity</h5>
  <p>If you use SCL tools or platforms (including AI tools, image studios, CRM systems, or marketing tools), you may create or upload content such as images, text, marketing materials, or client-related data ("User Content").</p>
  <p>SCL may access, review, and process User Content for legitimate business purposes, including:</p>
  <ul>
    <li>Providing and improving platform functionality</li>
    <li>Customer support and troubleshooting</li>
    <li>Monitoring system performance and security</li>
    <li>Ensuring compliance with applicable laws and policies</li>
  </ul>
  <p>User Content may be processed by automated systems, including AI tools, to support platform functionality and improve user experience.</p>
  <p>Access to User Content is limited to authorized personnel and is subject to internal access controls and security measures.</p>
  <p>We do not use User Content for independent marketing purposes outside of the services provided to you.</p>

  <h5>9. Marketing, Email, SMS, and Automated Communications</h5>
  <p><strong>Email Marketing</strong></p>
  <p>We may send marketing and informational email communications. You may unsubscribe at any time.</p>
  <p><strong>SMS / Text Messaging</strong></p>
  <p>We may send SMS messages for both transactional and marketing purposes.</p>
  <ul>
    <li>Message frequency varies based on your relationship with SCL</li>
    <li>Message and data rates may apply</li>
    <li>Reply STOP to opt out of all SMS communications</li>
    <li>Reply HELP for assistance</li>
    <li>You may also opt out by contacting us at info&#64;securechoicelending.com</li>
  </ul>
  <p><strong>Prior Express Written Consent:</strong> Before sending marketing SMS messages or contacting you via an automatic telephone dialing system (autodialer), prerecorded voice, artificially generated voice, or similar technologies, we will obtain your prior express written consent as required by applicable law.</p>
  <p><strong>Third-Party Leads:</strong> If we receive your contact information from a third-party source, we will only initiate automated or AI-assisted outreach after verifying that valid consent exists for contact by Secure Choice Lending, or after obtaining that consent directly from you.</p>
  <p>Consent to marketing communications is not required to obtain a loan or any other service from SCL.</p>
  <p><strong>Automated Communications</strong></p>
  <p>We may use automated systems, including AI, to deliver communications via email, SMS, or phone where permitted by law and where appropriate consent has been obtained.</p>

  <h5>10. Giveaways, Promotions, and Financial Incentives</h5>
  <p>We may offer promotions or giveaways.</p>
  <ul>
    <li>Entry does not require purchase</li>
    <li>Marketing opt-in is optional</li>
    <li>Personal information is used to administer promotions and communicate with participants</li>
  </ul>
  <p>Where required, we provide a Notice of Financial Incentive explaining the value exchange.</p>

  <h5>11. Your Privacy Rights</h5>
  <p>You have the right to:</p>
  <ul>
    <li>Access the personal information we collect about you</li>
    <li>Request correction of inaccurate information</li>
    <li>Request deletion of your personal information (subject to legal obligations)</li>
    <li>Opt out of marketing communications at any time</li>
  </ul>
  <p>To exercise these rights, contact us at:</p>
  <p class="mb-0">Email: info&#64;securechoicelending.com</p>
  <p>Phone: 951-733-8925</p>
  <p>We will verify your identity before processing requests.</p>

  <h5>12. California Privacy Rights (CPRA)</h5>
  <p>California residents have the right to:</p>
  <ul>
    <li>Know what personal information we collect and how we use it</li>
    <li>Request deletion of personal information</li>
    <li>Request correction of inaccurate information</li>
    <li>Opt out of "sharing" for advertising purposes</li>
    <li>Limit use of sensitive personal information</li>
    <li>Not be discriminated against for exercising rights</li>
  </ul>
  <p>To exercise your rights, contact us using the information above.</p>

  <h5>13. Data Retention</h5>
  <p>We retain personal information based on the type of data and applicable legal requirements:</p>
  <ul>
    <li>Mortgage and loan data: retained as required by federal and state law (typically 3&ndash;7+ years or longer where required)</li>
    <li>CRM and relationship data: retained while an active business relationship exists</li>
    <li>Marketing data: retained until you opt out or withdraw consent</li>
    <li>Giveaway and promotion data: retained for the duration of the promotion and a reasonable period thereafter</li>
    <li>Analytics data: may be aggregated and retained for internal reporting</li>
  </ul>
  <p>We may retain certain information longer where required for legal, regulatory, or compliance purposes.</p>

  <h5>14. Data Security</h5>
  <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>
  <p>We implement administrative, technical, and physical safeguards, including:</p>
  <ul>
    <li>Encryption of sensitive data</li>
    <li>Access controls and authentication</li>
    <li>Monitoring and security systems</li>
  </ul>

  <h5>15. Third-Party Links</h5>
  <p>We are not responsible for the privacy practices of third-party websites.</p>

  <h5>16. Updates to This Policy</h5>
  <p>We may update this policy periodically. Changes will be posted on this page with a revised effective date.</p>

  <h5>17. Contact Information</h5>
  <p class="mb-0">Secure Choice Lending</p>
  <p class="mb-0">1650 Spruce Street, Suite 500</p>
  <p class="mb-0">Riverside, CA 92507</p>
  <p class="mb-0">Email: info&#64;securechoicelending.com</p>
  <p>Phone: 951-733-8925</p>

  <h5>Appendix A: Notice at Collection (Short Form)</h5>
  <p>We collect personal information — including from third-party sources such as lead generators, credit bureaus, and advertising platforms — for mortgage lending, marketing, CRM operations, and service delivery purposes. We may use artificial intelligence to assist with communications and lending support functions. We may share data with service providers, advertising platforms, and lending participants. California residents and residents of certain other states have privacy rights under applicable law. See our full Privacy Policy for details.</p>

  <h5>Appendix B: Marketing and Communications Consent Language</h5>
  <p>By checking this box, I expressly consent to receive communications from Secure Choice Lending, including:</p>
  <ul>
    <li>Marketing and informational emails, which may be generated or personalized using artificial intelligence</li>
    <li>Marketing SMS/text messages sent using automated systems</li>
    <li>Prerecorded or artificially generated voice calls</li>
    <li>Automated or AI-assisted communications where permitted by law</li>
  </ul>
  <p>I understand that:</p>
  <ul>
    <li>Message and data rates may apply</li>
    <li>Message frequency varies</li>
    <li>I may opt out at any time by replying STOP to any text, clicking unsubscribe in any email, or contacting SCL at info&#64;securechoicelending.com or 951-733-8925</li>
  </ul>
  <p>My consent is not required as a condition of obtaining a loan or any other product or service from Secure Choice Lending.</p>

  <h5>Appendix C: Artificial Intelligence Disclosure</h5>
  <p>Secure Choice Lending uses artificial intelligence and automated systems to support communications and lending-related processes. AI may generate or assist in communications and support loan-related analysis, but does not replace human decision-making.</p>

  <h5>Appendix D: Giveaway Disclosure (Short Form)</h5>
  <p>Participation in promotions may involve collection of personal information. Marketing consent is optional. Full terms are provided in the official rules.</p>

  <h5>Appendix E: State-Specific Privacy Rights</h5>
  <p>Residents of certain states may have additional privacy rights. Contact us to exercise applicable rights under state law.</p>
</div>